import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MotionConfig } from "framer-motion";
import { Toaster } from "sonner";

import Nav from "./components/Nav";
import Hero from "./components/Hero";
import About from "./components/About";
import Skills from "./components/Skills";
import Certifications from "./components/Certifications";
import Experience from "./components/Experience";
import Projects from "./components/Projects";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import EditorialMarquee from "./components/EditorialMarquee";
import WhatsAppButton from "./components/WhatsAppButton";
import IntelTeaser from "./components/IntelTeaser";
import TechIntelligence from "./pages/TechIntelligence";
import WeeklyBriefPage from "./pages/WeeklyBriefPage";
import BriefArchivePage from "./pages/BriefArchivePage";
import { useLenis } from "./hooks/useLenis";
import { useTheme } from "./hooks/useTheme";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, retry: 2, refetchOnWindowFocus: false } },
});

const Home = ({ theme, toggleTheme }) => {
  useLenis();
  const location = useLocation();

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (!target) return;
    const t = setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    return () => clearTimeout(t);
  }, [location.state]);

  return (
    <div className="grain relative min-h-screen bg-background text-foreground selection:bg-accent selection:text-accent-foreground">
      <Nav theme={theme} toggleTheme={toggleTheme} />
      <main id="main-content" tabIndex={-1}>
        <Hero />
        <EditorialMarquee />
        <About />
        <Skills />
        <Certifications />
        <Experience />
        <Projects />
        <IntelTeaser />
        <Contact />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

function App() {
  const [theme, toggleTheme] = useTheme();
  return (
    <div className="App">
      <a href="#main-content" className="skip-link" data-testid="skip-to-content">
        Skip to content
      </a>
      <MotionConfig reducedMotion="user">
        <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home theme={theme} toggleTheme={toggleTheme} />} />
            <Route path="/intelligence" element={<TechIntelligence theme={theme} toggleTheme={toggleTheme} />} />
            <Route path="/brief/:week" element={<WeeklyBriefPage theme={theme} toggleTheme={toggleTheme} />} />
            <Route path="/briefs" element={<BriefArchivePage theme={theme} toggleTheme={toggleTheme} />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          theme={theme === "dark" ? "dark" : "light"}
          position="bottom-right"
          toastOptions={{
            style: {
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "12px",
              letterSpacing: "0.04em",
            },
          }}
        />
        </QueryClientProvider>
      </MotionConfig>
    </div>
  );
}

export default App;
